import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAboutSectorsData } from '@/hooks/useAboutSectorsData';
import { useLanguage } from '@/contexts/LanguageContext';
import { useScreenSize } from '@/hooks/use-mobile';
import { useResponsiveStyles } from '@/hooks/useSmartResponsive';
import { gsap } from 'gsap';

interface AboutSectorsCarouselProps {
  backgroundImage?: string;
  dataTheme?: 'light' | 'dark';
  sectionIndex?: number;
}

export function AboutSectorsCarousel({ 
  backgroundImage, 
  dataTheme = 'dark', // Force dark theme as requested
  sectionIndex = 0 
}: AboutSectorsCarouselProps) {
  const { data, isLoading } = useAboutSectorsData();
  const { language, isRTL } = useLanguage();
  const { isMobile } = useScreenSize();
  const responsiveConfig = useResponsiveStyles();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  const about = data?.about;
  const sectors = data?.sectors || [];

  // تطبيق CSS Variables للتحكم الديناميكي
  useEffect(() => {
    const root = document.documentElement;
    Object.entries(responsiveConfig.cssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // تحديث المتغيرات المتعلقة بالموبايل
    if (responsiveConfig.device.isMobile) {
      root.style.setProperty('--mobile-scale-factor', responsiveConfig.scales.combinedScale.toString());
      root.style.setProperty('--mobile-text-scale', responsiveConfig.scales.combinedScale.toString());
      root.style.setProperty('--mobile-header-offset', `${responsiveConfig.headerHeight}px`);
      root.style.setProperty('--mobile-available-height', `${responsiveConfig.availableHeight}px`);
      
      // تحديد ارتفاع الكاروسيل والحجم المربع بناءً على المساحة المتاحة
      const carouselHeightPercent = responsiveConfig.device.isVeryShortScreen ? 40 : 
                                   responsiveConfig.device.isShortScreen ? 43 : 46;
      root.style.setProperty('--mobile-carousel-height', `${carouselHeightPercent}vh`);
      root.style.setProperty('--mobile-square-size', `${responsiveConfig.mobileSquareSize}px`);
    }
  }, [responsiveConfig]);

  // Get offset for each sector relative to current index
  const getSectorOffset = useCallback((sectorIndex: number) => {
    if (sectors.length === 0) return 0;
    
    let offset = sectorIndex - currentIndex;
    
    // Handle circular wrapping
    if (offset > sectors.length / 2) {
      offset -= sectors.length;
    } else if (offset < -sectors.length / 2) {
      offset += sectors.length;
    }
    
    return offset;
  }, [sectors.length, currentIndex]);

  // Professional carousel navigation with smooth CSS transitions
  const navigate = useCallback((direction: 'next' | 'prev') => {
    if (isTransitioning || sectors.length === 0) return;
    
    setIsTransitioning(true);
    
    // Simple, smooth index update - let CSS handle the transitions
    setCurrentIndex(prev => {
      if (direction === 'next') {
        return (prev + 1) % sectors.length;
      } else {
        return prev === 0 ? sectors.length - 1 : prev - 1;
      }
    });
    
    // Reset transitioning state after CSS transition completes
    setTimeout(() => setIsTransitioning(false), 800);
  }, [isTransitioning, sectors.length]);

  // Auto-play functionality
  useEffect(() => {
    if (isPaused || sectors.length <= 1) return;
    
    const interval = setInterval(() => {
      navigate('next');
    }, 4000);
    
    return () => clearInterval(interval);
  }, [navigate, isPaused, sectors.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        navigate(isRTL ? 'next' : 'prev');
      } else if (e.key === 'ArrowRight') {
        navigate(isRTL ? 'prev' : 'next');
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate, isRTL]);


  if (isLoading) {
    return (
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        }}
        data-theme={dataTheme}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-background/80 to-background/60 backdrop-blur-sm" />
        <div className="relative z-10 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </section>
    );
  }

  

  return (
    <section 
      className="relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        paddingTop: isMobile ? `${Math.max(responsiveConfig.headerHeight + 20, 80)}px` : '0px',
        minHeight: isMobile ? `${responsiveConfig.availableHeight + responsiveConfig.headerHeight}px` : '100vh'
      }}
      data-theme="dark"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Overlay - for light background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1f336366] via-[#172b57e0] to-[#081128f2] backdrop-blur-sm" />
      
      {/* Content Container */}
      <div className="relative z-20 h-full flex flex-col justify-center">
        
        {/* Upper Section - Two Text Paragraphs with Smart Responsive Layout */}
        <div className={`${isMobile ? 'flex-none' : 'md:flex-1'} flex items-center`}
             style={{
               paddingTop: isMobile ? responsiveConfig.getSpacing('elements') : '',
               paddingBottom: isMobile ? responsiveConfig.getSpacing('elements') : '',
               minHeight: isMobile ? `${Math.min(responsiveConfig.availableHeight * 0.4, 250)}px` : 'auto'
             }}>
          <div className={`container mx-auto px-4 sm:px-6 md:px-8 max-w-6xl`}
               style={{
                 padding: isMobile ? responsiveConfig.getSpacing('container') : '1.5rem 2rem'
               }}>
            <div className={`grid lg:grid-cols-2 items-center justify-items-center ${isRTL ? 'lg:grid-flow-col-dense' : ''}`}
                 style={{
                   gap: isMobile ? responsiveConfig.getSpacing('elements') : '3rem'
                 }}>
              
              {/* First Text Column - Label + Headline + CTA */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? 50 : -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                className={`${isRTL ? 'lg:col-start-2 text-right' : 'text-left'} w-full mx-auto ${isRTL ? 'lg:mr-auto lg:pr-8 xl:pr-12' : 'lg:ml-auto lg:pl-8 xl:pl-12'}`}
                style={{
                  maxWidth: isMobile ? '100%' : '32rem',
                  gap: responsiveConfig.getSpacing('elements'),
                  padding: isMobile ? `0 ${responsiveConfig.getSpacing('container')}` : '0 1.5rem'
                }}
              >
                {/* Label */}
                <div className="font-medium uppercase tracking-wide text-white/80"
                     style={{ 
                       fontSize: responsiveConfig.getTextSize('small'),
                       marginBottom: responsiveConfig.getSpacing('elements')
                     }}>
                  {language === 'ar' ? about?.label_ar : about?.label_en}
                </div>
                
                {/* Headline */}
                <h2 className={`font-bold leading-tight text-white ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}
                    style={{ 
                      fontSize: responsiveConfig.getTextSize('hero'),
                      marginBottom: responsiveConfig.getSpacing('elements')
                    }}>
                  {language === 'ar' ? about?.headline_ar : about?.headline_en}
                </h2>
                
                {/* CTA Button */}
                <button 
                  className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 border border-white/30 hover:border-white/50 shadow-white/20 hover:shadow-white/30 backdrop-blur-sm inline-flex items-center rounded-lg transition-all duration-300"
                  style={{
                    fontSize: responsiveConfig.getTextSize('body'),
                    padding: `${responsiveConfig.getSpacing('elements')} ${responsiveConfig.getSpacing('container')}`,
                    gap: responsiveConfig.getSpacing('elements')
                  }}
                  onClick={() => {
                    const href = about?.cta_href || '/about';
                    if (href.startsWith('#')) {
                      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      window.location.href = href;
                    }
                  }}
                >
                  {language === 'ar' ? about?.cta_text_ar : about?.cta_text_en}
                  <svg className={`w-4 h-4 transition-transform ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </motion.div>

              {/* Second Text Column - Counter + Blurb */}
              <motion.div
                initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className={`${isRTL ? 'lg:col-start-1 text-right' : 'text-left'} w-full mx-auto ${isRTL ? 'lg:ml-auto lg:pl-8 xl:pl-12' : 'lg:mr-auto lg:pr-8 xl:pr-12'}`}
                style={{
                  maxWidth: isMobile ? '100%' : '28rem',
                  gap: responsiveConfig.getSpacing('elements'),
                  padding: isMobile ? `0 ${responsiveConfig.getSpacing('container')}` : '0 1.5rem'
                }}
              >
                {/* Animated Counter */}
                <div style={{ marginBottom: responsiveConfig.getSpacing('elements') }}>
                  <div className={`font-bold text-white ${language === 'ar' ? 'font-arabic-heading' : 'font-english-heading'}`}
                       style={{
                         fontSize: `calc(${responsiveConfig.getTextSize('hero')} * 1.8)`,
                         marginBottom: responsiveConfig.getSpacing('elements')
                       }}>
                    {about?.years || 80}+
                  </div>
                  <div className="font-medium text-white/80"
                       style={{ fontSize: responsiveConfig.getTextSize('heading') }}>
                    {language === 'ar' ? 'عاماً من الخبرة' : 'Years of Excellence'}
                  </div>
                </div>
                
                {/* Blurb */}
                <p className={`leading-relaxed text-white/85 ${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}
                   style={{
                     fontSize: responsiveConfig.getTextSize('body'),
                     maxWidth: '100%',
                     lineHeight: 1.6
                   }}>
                  {language === 'ar' ? about?.blurb_ar : about?.blurb_en}
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Lower Section - 3D Carousel with Smart Responsive Design */}
        <div className={`${isMobile ? 'flex-none' : 'md:flex-1'} flex items-center justify-center`}
             style={{
               minHeight: isMobile ? responsiveConfig.carouselDimensions.container.height : 'auto',
               maxHeight: isMobile ? `${responsiveConfig.availableHeight * 0.6}px` : 'auto',
               padding: `${responsiveConfig.getSpacing('elements')} 0`
             }}>
          <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-6xl w-full"
               style={{
                 padding: `${responsiveConfig.getSpacing('container')} 1rem`
               }}>
            <div className={`sectors-carousel-3d-container ${isMobile ? 'mobile-single-view' : ''}`} ref={carouselRef}>
              <div className="sectors-carousel-3d-stage">
                {isMobile ? (
                  // Mobile: Single centered card
                  <div
                    key={`mobile-${sectors[currentIndex]?.slug}`}
                    className="sectors-carousel-item sectors-carousel-item-mobile-center"
                    onClick={() => {
                      const currentSector = sectors[currentIndex];
                      if (currentSector) {
                        window.location.href = `/sectors/${currentSector.slug}`;
                      }
                    }}
                  >
                    <div className="sectors-carousel-card sectors-carousel-card-mobile">
                      <img
                        src={sectors[currentIndex]?.image}
                        alt={language === 'ar' ? sectors[currentIndex]?.name_ar : sectors[currentIndex]?.name_en}
                        className="sectors-carousel-image"
                        loading="lazy"
                      />
                      <div className="sectors-carousel-overlay" />
                      <div className="sectors-carousel-content sectors-carousel-content-mobile">
                        {sectors[currentIndex]?.icon_url && (
                          <img
                            src={sectors[currentIndex].icon_url}
                            alt=""
                            className="sectors-carousel-icon"
                          />
                        )}
                        <h3 className="sectors-carousel-title">
                          {language === 'ar' ? sectors[currentIndex]?.name_ar : sectors[currentIndex]?.name_en}
                        </h3>
                        <p className="sectors-carousel-description line-clamp-2">
                          {language === 'ar' ? sectors[currentIndex]?.excerpt_ar : sectors[currentIndex]?.excerpt_en}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  // Desktop: All sectors with stable keys and CSS positioning
                  sectors.map((sector, sectorIndex) => {
                    const offset = getSectorOffset(sectorIndex);
                    
                    // Determine CSS class based on offset
                    let positionClass = '';
                    let isVisible = true;
                    
                    if (offset === 0) {
                      positionClass = 'sectors-carousel-item-center';
                    } else if (offset === -1) {
                      positionClass = isRTL ? 'sectors-carousel-item-side-right' : 'sectors-carousel-item-side-left';
                    } else if (offset === 1) {
                      positionClass = isRTL ? 'sectors-carousel-item-side-left' : 'sectors-carousel-item-side-right';
                    } else {
                      positionClass = 'sectors-carousel-item-hidden';
                      isVisible = false;
                    }

                    return (
                      <div
                        key={sector.slug}
                        className={`sectors-carousel-item ${positionClass}`}
                        onClick={() => {
                          if (offset === 0) {
                            // Navigate to sector page
                            window.location.href = `/sectors/${sector.slug}`;
                          } else if (Math.abs(offset) === 1) {
                            // Make clicked sector the center one
                            setCurrentIndex(sectorIndex);
                          }
                        }}
                        style={{
                          opacity: isVisible ? 1 : 0,
                          pointerEvents: isVisible ? 'auto' : 'none',
                        }}
                      >
                        <div className={`sectors-carousel-card ${
                          offset === 0 ? 'sectors-carousel-card-center' : 'sectors-carousel-card-side'
                        }`}>
                          <img
                            src={sector.image}
                            alt={language === 'ar' ? sector.name_ar : sector.name_en}
                            className="sectors-carousel-image"
                            loading="lazy"
                          />
                          <div className={`sectors-carousel-overlay ${
                            offset === 0 ? '' : 'sectors-carousel-overlay-side'
                          }`} />
                          <div className={`sectors-carousel-content ${
                            offset === 0 ? 'sectors-carousel-content-center' : 'sectors-carousel-content-side'
                          }`}>
                            {sector.icon_url && (
                              <img
                                src={sector.icon_url}
                                alt=""
                                className="sectors-carousel-icon"
                              />
                            )}
                            <h3 className="sectors-carousel-title">
                              {language === 'ar' ? sector.name_ar : sector.name_en}
                            </h3>
                            {offset === 0 && (
                              <p className="sectors-carousel-description line-clamp-2">
                                {language === 'ar' ? sector.excerpt_ar : sector.excerpt_en}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}

                {/* Navigation Arrows */}
                <button
                  className={`sectors-carousel-nav ${isRTL ? 'right-4' : 'left-4'}`}
                  onClick={() => navigate(isRTL ? 'next' : 'prev')}
                  aria-label={isRTL ? 'التالي' : 'Previous'}
                >
                  {isRTL ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                </button>
                <button
                  className={`sectors-carousel-nav ${isRTL ? 'left-4' : 'right-4'}`}
                  onClick={() => navigate(isRTL ? 'prev' : 'next')}
                  aria-label={isRTL ? 'السابق' : 'Next'}
                >
                  {isRTL ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
                </button>

                {/* Dots Indicator */}
                <div className="sectors-carousel-dots">
                  {sectors.map((_, index) => (
                    <button
                      key={index}
                      className={`sectors-carousel-dot ${
                        index === currentIndex ? 'sectors-carousel-dot-active' : ''
                      }`}
                      onClick={() => setCurrentIndex(index)}
                      aria-label={`Go to sector ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}